import axios, { AxiosError } from 'axios';

const TRANSIENT_HTTP_STATUSES = new Set([408, 429, 500, 502, 503, 504]);

export type HttpErrorKind = 'transient' | 'auth' | 'forbidden' | 'client' | 'unknown';

/**
 * MSAL throws InteractionRequiredAuthError when silent token refresh fails because
 * the user session no longer exists (e.g. AADSTS160021). These are not Axios errors,
 * so we detect them by shape and treat as 'auth' so the app triggers interactive login.
 */
function isMsalInteractionRequiredError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { errorCode?: string; name?: string; message?: string };
  return (
    e.errorCode === 'interaction_required' ||
    e.name === 'InteractionRequiredAuthError' ||
    (typeof e.message === 'string' &&
      (e.message.includes('interaction_required') || e.message.includes('AADSTS160021')))
  );
}

export function getHttpErrorKind(error: unknown): HttpErrorKind {
  if (isMsalInteractionRequiredError(error)) return 'auth';
  if (!axios.isAxiosError(error)) return 'unknown';

  const status = (error as AxiosError).response?.status;
  // No response = network/CORS/offline; 4xx/5xx retryable; 5xx server errors
  if (status == null || TRANSIENT_HTTP_STATUSES.has(status) || status >= 500) return 'transient';
  if (status === 401) return 'auth';
  if (status === 403) return 'forbidden';
  if (status >= 400 && status < 500) return 'client';

  return 'unknown';
}

export function isTransientHttpError(error: unknown): boolean {
  return getHttpErrorKind(error) === 'transient';
}

